import type {
  AysCodeCodingSystem,
  AysCodingSystemSpecification,
} from '@bindoc-projects/code-domain-lib';
import type { AysCodeSpectrumMatching } from '@bindoc-projects/code-spectrum-domain-lib';
import { ShareableType } from '@bindoc-projects/resource-domain-lib';
import { BdArrayUtil, BdEquals } from '@bindoc-projects/util-domain-lib';
import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import type {
  FilterConditionDto,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdCondition
} from '@bindoc-projects/condition-domain-lib';
import { CODE_SPECTRUM_CONDITION_ID } from '../common-plugins';
import type { FilterDetailedText } from '../filter-detailed-text';

export const FilterConditionCodeSpectrumChPluginType: FilterConditionPluginSpecification<
  FilterConditionCodeSpectrumChObject,
  FilterConditionCodeSpectrumChDto,
  FilterConditionCodeSpectrumChConditionJson
> = {
  type: 'codes-ch',
  category: 'case',
  displayName: { de: 'Ziffer-Spektra' },
  sortingIndex: 8,
  occurrence: 'multiple',
  fromObject(
    obj: FilterConditionCodeSpectrumChDto,
  ): FilterConditionPluginImpl<FilterConditionCodeSpectrumChObject> {
    const codingSystem: AysCodeCodingSystem = obj.codingSystem || 'ICD-10-CH';
    const matching = obj.matching || 'exact';

    return new FilterConditionCodeSpectrumChPlugin(
      obj.codeSpectrumIds || [],
      codingSystem,
      obj.codeTypeSpecification,
      matching,
      obj.includes,
    );
  },
  fromCondition(
    jsonObj: FilterConditionCodeSpectrumChConditionJson,
  ): FilterConditionCodeSpectrumChPlugin {
    return new FilterConditionCodeSpectrumChPlugin(
      jsonObj.includes ?? jsonObj.excludes,
      jsonObj.codingSystem,
      jsonObj.codeTypeSpecification,
      jsonObj.matching,
      !(jsonObj.excludes && Array.isArray(jsonObj.excludes)),
    );
  },
  newInstance(): FilterConditionPluginImpl<FilterConditionCodeSpectrumChObject> {
    return new FilterConditionCodeSpectrumChPlugin([], 'ICD-10-CH');
  },
};

export class FilterConditionCodeSpectrumChPlugin
  extends FilterConditionPluginImpl<
    FilterConditionCodeSpectrumChObject,
    FilterConditionCodeSpectrumChDto
  >
  implements FilterConditionCodeSpectrumChObject
{
  static override Specification: FilterConditionPluginSpecification<
    FilterConditionCodeSpectrumChObject,
    FilterConditionCodeSpectrumChDto,
    FilterConditionCodeSpectrumChConditionJson
  > = FilterConditionCodeSpectrumChPluginType;

  constructor(
    public codeSpectrumIds: string[],
    public codingSystem: AysCodeCodingSystem,
    public codeTypeSpecification?: AysCodingSystemSpecification,
    public matching: AysCodeSpectrumMatching = 'exact',
    public includes = true,
  ) {
    super(FilterConditionCodeSpectrumChPluginType.type);
  }

  clone(): FilterConditionCodeSpectrumChPlugin {
    return new FilterConditionCodeSpectrumChPlugin(
      [...this.codeSpectrumIds],
      this.codingSystem,
      this.codeTypeSpecification,
      this.matching,
      this.includes,
    );
  }

  override equals(other: FilterConditionCodeSpectrumChPlugin): boolean {
    return (
      super.equals(other) &&
      BdArrayUtil.equals(this.codeSpectrumIds, other.codeSpectrumIds) &&
      BdEquals.equals(this.codingSystem, other.codingSystem) &&
      BdEquals.equals(
        this.codeTypeSpecification,
        other.codeTypeSpecification,
      ) &&
      BdEquals.equals(this.matching, other.matching) &&
      BdEquals.equals(this.includes, other.includes)
    );
  }

  override getChildResources(): {
    [resourceName in ShareableType]?: string[];
  } {
    const codeSpectrumIds: string[] = [];

    this.codeSpectrumIds.forEach((codeSpectrumId) => {
      if (codeSpectrumIds.indexOf(codeSpectrumId) === -1) {
        codeSpectrumIds.push(codeSpectrumId);
      }
    });

    return { [ShareableType.CODE_SPECTRUM]: codeSpectrumIds };
  }

  override valid(): boolean {
    return (
      super.valid() && this.codeSpectrumIds && this.codeSpectrumIds.length > 0
    );
  }

  override validationToText(): string {
    if (this.codeSpectrumIds.length === 0) {
      return 'Mindestens ein Ziffer-Spektrum auswählen';
    } else {
      return '';
    }
  }

  override toDetailedText(resolvedNames?: Map<string, string>): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    const items = this.codeSpectrumIds.map(
      (id) => resolvedNames?.get(id) ?? id,
    );
    return { title, items };
  }

  toText(): string | null {
    return this.codeSpectrumIds.length === 1
      ? 'ein Ziffer-Spektrum'
      : `${this.codeSpectrumIds.length} Ziffer-Spektra`;
  }

  toJSON(): FilterConditionCodeSpectrumChDto | null {
    return {
      type: this.type,
      codingSystem: this.codingSystem,
      codeSpectrumIds: this.codeSpectrumIds,
      codeTypeSpecification: this.codeTypeSpecification,
      matching: this.matching,
      includes: this.includes,
    };
  }

  toValue(): FilterConditionCodeSpectrumChObject {
    return this;
  }

  public toCondition(): BdCondition {
    return new FilterConditionCodeSpectrumChCondition(this);
  }
}

export class FilterConditionCodeSpectrumChCondition extends BdCondition {
  constructor(private _codeSpectrum: FilterConditionCodeSpectrumChObject) {
    super(CODE_SPECTRUM_CONDITION_ID);
  }

  toJSON(): FilterConditionDto {
    return {
      [this.id]: {
        includes: this._codeSpectrum.includes
          ? this._codeSpectrum.codeSpectrumIds
          : undefined,
        excludes: this._codeSpectrum.includes
          ? undefined
          : this._codeSpectrum.codeSpectrumIds,
        codingSystem: this._codeSpectrum.codingSystem,
        matching: this._codeSpectrum.matching,
        codeTypeSpecification: this._codeSpectrum.codeTypeSpecification,
      },
    };
  }
}

interface FilterConditionCodeSpectrumChObject {
  codeSpectrumIds: string[];
  codingSystem: AysCodeCodingSystem;
  codeTypeSpecification?: AysCodingSystemSpecification;
  matching: AysCodeSpectrumMatching;
  includes?: boolean;
}

interface FilterConditionCodeSpectrumChDto
  extends FilterConditionCodeSpectrumChObject {
  type: string;
}

interface FilterConditionCodeSpectrumChConditionJson
  extends Omit<FilterConditionCodeSpectrumChObject, 'includes'> {
  includes: string[] | undefined;
  excludes: string[] | undefined;
}
